/**
 * 通知域 Mock 数据
 * 根据 notification/frontend-spec.md 规范定义
 */

import type { ApiResponse, PageData } from '@/types/common';
import type {
  NotificationRecordDTO,
  QueryNotificationRecordPageRequest,
  ResendNotificationRequest,
  SendNotificationRequest,
  QueryNotificationStatisticsRequest,
  NotificationStatisticsDTO,
} from '@/types/notification/notificationRecord';
import type {
  NotificationTemplateDTO,
  QueryNotificationTemplatePageRequest,
  UpdateNotificationTemplateRequest,
} from '@/types/notification/notificationTemplate';
import { NotificationType, NotificationStatus, NotificationChannel, NotificationPriority } from '@/constants/notification';

/**
 * Mock 工具函数
 */
function mockSuccess<T>(data: T): ApiResponse<T> {
  return {
    success: true,
    errorMessage: null,
    flag: 'success',
    data,
    traceId: `mock-${Date.now()}`,
  };
}

/**
 * Mock 通知记录数据
 * 只保留两条未读通知：新订单下发、退货通知
 */
const mockNotificationRecords: NotificationRecordDTO[] = [
  {
    id: 1,
    code: 'NTF20260307001',
    notificationId: 'NTF20260307001',
    notificationType: NotificationType.CREATE_ORDER,
    channel: NotificationChannel.IN_APP,
    priority: NotificationPriority.HIGH,
    targetType: 'USER',
    targetId: 'USR001',
    targetName: 'Current User',
    title: 'New Order Received',
    content: 'A new order CPTH7006794286 has been placed and is waiting for processing.',
    status: NotificationStatus.DELIVERED,
    scheduledTime: null,
    sentTime: Date.now() - 3600000,
    deliveredTime: Date.now() - 3600000,
    readTime: null, // 未读
    failReason: null,
    retryCount: 0,
    relatedOrderNo: 'CPTH7006794286',
    storeCode: 'STORE001',
    storeName: 'Lotus Store A',
    creatorAccount: 'system',
    creatorName: 'System',
    gmtCreate: Date.now() - 3600000,
    modifierAccount: 'system',
    modifierName: 'System',
    gmtModified: Date.now() - 3600000,
  },
  {
    id: 2,
    code: 'NTF20260307002',
    notificationId: 'NTF20260307002',
    notificationType: NotificationType.RETURN_ORDER,
    channel: NotificationChannel.IN_APP,
    priority: NotificationPriority.NORMAL,
    targetType: 'USER',
    targetId: 'USR001',
    targetName: 'Current User',
    title: 'Return Request Received',
    content: 'A return request for order CPTH7006794287 has been received and requires your attention.',
    status: NotificationStatus.DELIVERED,
    scheduledTime: null,
    sentTime: Date.now() - 7200000,
    deliveredTime: Date.now() - 7200000,
    readTime: null, // 未读
    failReason: null,
    retryCount: 0,
    relatedOrderNo: 'CPTH7006794287',
    storeCode: 'STORE001',
    storeName: 'Lotus Store A',
    creatorAccount: 'system',
    creatorName: 'System',
    gmtCreate: Date.now() - 7200000,
    modifierAccount: 'system',
    modifierName: 'System',
    gmtModified: Date.now() - 7200000,
  },
  {
    id: 3,
    code: 'NTF20260307003',
    notificationId: 'NTF20260307003',
    notificationType: NotificationType.CANCEL_ORDER,
    channel: NotificationChannel.IN_APP,
    priority: NotificationPriority.HIGH,
    targetType: 'USER',
    targetId: 'USR001',
    targetName: 'Current User',
    title: 'Order Cancelled',
    content: 'Order CPTH7006794288 has been cancelled. Please handle it promptly to avoid affecting warehouse operations.',
    status: NotificationStatus.DELIVERED,
    scheduledTime: null,
    sentTime: Date.now() - 1800000,
    deliveredTime: Date.now() - 1800000,
    readTime: null, // 未读
    failReason: null,
    retryCount: 0,
    relatedOrderNo: 'CPTH7006794288',
    storeCode: 'STORE001',
    storeName: 'Lotus Store A',
    creatorAccount: 'system',
    creatorName: 'System',
    gmtCreate: Date.now() - 1800000,
    modifierAccount: 'system',
    modifierName: 'System',
    gmtModified: Date.now() - 1800000,
  },
  {
    id: 4,
    code: 'NTF20260307004',
    notificationId: 'NTF20260307004',
    notificationType: NotificationType.APPROVAL,
    channel: NotificationChannel.IN_APP,
    priority: NotificationPriority.NORMAL,
    targetType: 'USER',
    targetId: 'USR001',
    targetName: 'Current User',
    title: 'Temporary Staff Registration',
    content: 'A new temporary staff member has registered and is pending approval. Please review and approve in a timely manner.',
    status: NotificationStatus.DELIVERED,
    scheduledTime: null,
    sentTime: Date.now() - 900000,
    deliveredTime: Date.now() - 900000,
    readTime: null, // 未读
    failReason: null,
    retryCount: 0,
    relatedOrderNo: null,
    storeCode: 'STORE001',
    storeName: 'Lotus Store A',
    creatorAccount: 'system',
    creatorName: 'System',
    gmtCreate: Date.now() - 900000,
    modifierAccount: 'system',
    modifierName: 'System',
    gmtModified: Date.now() - 900000,
  },
];

/**
 * Mock 通知模板数据
 */
const mockNotificationTemplates: NotificationTemplateDTO[] = [
  {
    id: 1,
    code: 'TPL_TMS_PICKUP_READY',
    templateCode: 'TPL_TMS_PICKUP_READY',
    templateName: 'TMS Pickup Ready Notification',
    notificationType: NotificationType.CREATE_ORDER,
    channel: NotificationChannel.PUSH,
    titleTemplate: 'New Pickup Ready',
    contentTemplate: 'Order ${orderNo} is ready for pickup at ${storeName}. Please arrive within ${timeLimit} minutes.',
    variables: ['orderNo', 'storeName', 'timeLimit'],
    isEnabled: true,
    creatorAccount: 'admin',
    creatorName: 'Administrator',
    gmtCreate: 1709200000000,
    modifierAccount: 'admin',
    modifierName: 'Administrator',
    gmtModified: 1709200000000,
  },
  {
    id: 2,
    code: 'TPL_CUSTOMER_ORDER_PICKED',
    templateCode: 'TPL_CUSTOMER_ORDER_PICKED',
    templateName: 'Customer Order Picked Notification',
    notificationType: NotificationType.CREATE_ORDER,
    channel: NotificationChannel.PUSH,
    titleTemplate: 'Order ${orderNo} Picked',
    contentTemplate: 'Your order ${orderNo} has been picked and will be delivered by ${riderName}.',
    variables: ['orderNo', 'riderName'],
    isEnabled: true,
    creatorAccount: 'admin',
    creatorName: 'Administrator',
    gmtCreate: 1709200000000,
    modifierAccount: 'admin',
    modifierName: 'Administrator',
    gmtModified: 1709200000000,
  },
  {
    id: 3,
    code: 'TPL_SYSTEM_SHORTAGE_ALERT',
    templateCode: 'TPL_SYSTEM_SHORTAGE_ALERT',
    templateName: 'System Shortage Alert',
    notificationType: NotificationType.APPROVAL,
    channel: NotificationChannel.EMAIL,
    titleTemplate: 'High Shortage Rate Alert - ${storeName}',
    contentTemplate: 'Store ${storeName} has a shortage rate of ${shortageRate}% today, above threshold ${threshold}%.',
    variables: ['storeName', 'shortageRate', 'threshold'],
    isEnabled: false,
    creatorAccount: 'admin',
    creatorName: 'Administrator',
    gmtCreate: 1709200000000,
    modifierAccount: 'admin',
    modifierName: 'Administrator',
    gmtModified: 1709250000000,
  },
];

/**
 * 分页查询通知记录 Mock
 */
export function mockPageNotificationRecords(
  params: QueryNotificationRecordPageRequest
): Promise<ApiResponse<PageData<NotificationRecordDTO>>> {
  const { currentPage = 1, pageSize = 20, notificationType, channel, status } = params;
  let filtered = [...mockNotificationRecords];

  if (notificationType) {
    filtered = filtered.filter(r => r.notificationType === notificationType);
  }
  if (channel) {
    filtered = filtered.filter(r => r.channel === channel);
  }
  if (status) {
    filtered = filtered.filter(r => r.status === status);
  }

  const start = (currentPage - 1) * pageSize;
  const end = start + pageSize;
  const tableData = filtered.slice(start, end);

  return Promise.resolve(mockSuccess({
    tableData,
    paging: { currentPage, pageSize, totalCount: filtered.length },
  }));
}

/**
 * 查询通知详情 Mock
 */
export function mockGetNotificationRecordById(
  notificationId: string
): Promise<ApiResponse<NotificationRecordDTO>> {
  const record = mockNotificationRecords.find(r => r.notificationId === notificationId);
  if (record) {
    return Promise.resolve(mockSuccess(record));
  }
  return Promise.resolve({
    success: false,
    errorMessage: 'Notification not found',
    flag: 'fail',
    data: null,
    traceId: `mock-${Date.now()}`,
  });
}

/**
 * 重发通知 Mock
 */
export function mockResendNotifications(
  data: ResendNotificationRequest
): Promise<ApiResponse<null>> {
  return Promise.resolve(mockSuccess(null));
}

/**
 * 手动发送通知 Mock
 */
export function mockSendNotification(
  data: SendNotificationRequest
): Promise<ApiResponse<NotificationRecordDTO>> {
  const newRecord: NotificationRecordDTO = {
    id: Date.now(),
    code: `NTF${Date.now()}`,
    notificationId: `NTF${Date.now()}`,
    notificationType: NotificationType.CREATE_ORDER,
    channel: NotificationChannel.PUSH,
    priority: NotificationPriority.NORMAL,
    targetType: data.targetType,
    targetId: data.targetId,
    targetName: 'Target Name',
    title: 'Notification Title',
    content: 'Notification Content',
    status: NotificationStatus.PENDING,
    scheduledTime: data.scheduledTime || null,
    sentTime: null,
    deliveredTime: null,
    readTime: null,
    failReason: null,
    retryCount: 0,
    relatedOrderNo: null,
    storeCode: 'STORE001',
    storeName: 'Lotus Store A',
    creatorAccount: 'current_user',
    creatorName: 'Current User',
    gmtCreate: Date.now(),
    modifierAccount: 'current_user',
    modifierName: 'Current User',
    gmtModified: Date.now(),
  };
  return Promise.resolve(mockSuccess(newRecord));
}

/**
 * 查询发送统计 Mock
 */
export function mockQueryNotificationStatistics(
  params: QueryNotificationStatisticsRequest
): Promise<ApiResponse<NotificationStatisticsDTO[]>> {
  const stats: NotificationStatisticsDTO[] = [
    {
      date: params.startDate,
      storeCode: params.storeCode || 'STORE001',
      notificationType: NotificationType.CREATE_ORDER,
      channel: NotificationChannel.PUSH,
      totalCount: 150,
      sentCount: 148,
      deliveredCount: 145,
      failedCount: 2,
      deliveryRate: 96.67,
    },
    {
      date: params.startDate,
      storeCode: params.storeCode || 'STORE001',
      notificationType: NotificationType.CANCEL_ORDER,
      channel: NotificationChannel.PUSH,
      totalCount: 320,
      sentCount: 318,
      deliveredCount: 310,
      failedCount: 2,
      deliveryRate: 96.88,
    },
  ];
  return Promise.resolve(mockSuccess(stats));
}

/**
 * 分页查询通知模板 Mock
 */
export function mockPageNotificationTemplates(
  params: QueryNotificationTemplatePageRequest
): Promise<ApiResponse<PageData<NotificationTemplateDTO>>> {
  const { currentPage = 1, pageSize = 20, notificationType, channel, isEnabled } = params;
  let filtered = [...mockNotificationTemplates];

  if (notificationType) {
    filtered = filtered.filter(t => t.notificationType === notificationType);
  }
  if (channel) {
    filtered = filtered.filter(t => t.channel === channel);
  }
  if (isEnabled !== undefined) {
    filtered = filtered.filter(t => t.isEnabled === isEnabled);
  }

  const start = (currentPage - 1) * pageSize;
  const end = start + pageSize;
  const tableData = filtered.slice(start, end);

  return Promise.resolve(mockSuccess({
    tableData,
    paging: { currentPage, pageSize, totalCount: filtered.length },
  }));
}

/**
 * 编辑通知模板 Mock
 */
export function mockUpdateNotificationTemplate(
  data: UpdateNotificationTemplateRequest
): Promise<ApiResponse<NotificationTemplateDTO>> {
  const template = mockNotificationTemplates.find(t => t.templateCode === data.templateCode);
  if (template) {
    const updated = { ...template, ...data, gmtModified: Date.now() };
    return Promise.resolve(mockSuccess(updated));
  }
  return Promise.resolve({
    success: false,
    errorMessage: 'Template not found',
    flag: 'fail',
    data: null,
    traceId: `mock-${Date.now()}`,
  });
}

/**
 * 标记单条通知为已读 Mock
 */
export function mockMarkNotificationAsRead(
  notificationId: string
): Promise<ApiResponse<null>> {
  const record = mockNotificationRecords.find(r => r.notificationId === notificationId);
  if (record) {
    record.readTime = Date.now();
    record.status = NotificationStatus.READ;
  }
  return Promise.resolve(mockSuccess(null));
}

/**
 * 一键已读 Mock
 */
export function mockMarkAllNotificationsAsRead(): Promise<ApiResponse<null>> {
  mockNotificationRecords.forEach(record => {
    if (!record.readTime) {
      record.readTime = Date.now();
      record.status = NotificationStatus.READ;
    }
  });
  return Promise.resolve(mockSuccess(null));
}

/**
 * 获取未读通知数量 Mock
 */
export function mockGetUnreadNotificationCount(): Promise<ApiResponse<number>> {
  const unreadCount = mockNotificationRecords.filter(r => !r.readTime).length;
  return Promise.resolve(mockSuccess(unreadCount));
}
